import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import RegistrationProgressIndicator from '../../components/ui/RegistrationProgressIndicator';
import SchoolDetailsStep from './components/SchoolDetailsStep';
import CoachInformationStep from './components/CoachInformationStep';
import PlayerRegistrationStep from './components/PlayerRegistrationStep';
import CaptainSelectionStep from './components/CaptainSelectionStep';
import FormNavigationControls from './components/FormNavigationControls';
import Icon from '../../components/AppIcon';
import { registrationService } from '../../services/registrationService';
import { setMobileSession } from '../../lib/supabase';

const TeamRegistrationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);
  const [error, setError] = useState(null);
  const [isResubmission, setIsResubmission] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  // Get mobile number from auth context or localStorage
  const mobileNumber = localStorage.getItem('userMobile') || '9876543210';

  const [formData, setFormData] = useState({
    ageCategory: '',
    schoolName: '',
    schoolId: null,
    schoolEmail: '',
    city: '',
    primaryCoachName: '',
    primaryCoachMobile: mobileNumber,
    primaryCoachEmail: '',
    primaryCoachTshirtSize: '',
    hasSecondCoach: false,
    secondCoachName: '',
    secondCoachMobile: '',
    secondCoachEmail: '',
    secondCoachTshirtSize: '',
    players: [],
    captainId: ''
  });

  const [errors, setErrors] = useState({});

  // Load existing draft on mount and when registrationId changes
  useEffect(() => {
    loadDraft();
  }, [location?.state?.registrationId]);

  // Reload players when entering step 4 (Captain Selection)
  useEffect(() => {
    const reloadPlayersForCaptainSelection = async () => {
      if (currentStep === 4 && registrationId) {
        try {
          const players = await registrationService?.getPlayersByRegistration(registrationId);
          const playersWithName = players?.map(p => ({
            id: p?.id,
            dbId: p?.id,
            name: `${p?.first_name} ${p?.middle_name || ''} ${p?.last_name}`.trim(),
            firstName: p?.first_name,
            middleName: p?.middle_name,
            lastName: p?.last_name,
            dob: p?.date_of_birth,
            mobile: p?.mobile_number,
            email: p?.email,
            mcaId: p?.mca_id,
            role: p?.role,
            tshirtSize: p?.tshirt_size,
            pantSize: p?.pant_size,
            isCaptain: p?.is_captain
          })) || [];
          
          setFormData(prev => ({
            ...prev,
            players: playersWithName
          }));
        } catch (error) {
          console.error('Error reloading players for captain selection:', error);
        }
      }
    };

    reloadPlayersForCaptainSelection();
  }, [currentStep, registrationId]);

  const loadDraft = async () => {
    try {
      const registrationIdFromState = location?.state?.registrationId;
      const isNewRegistration = location?.state?.isNewRegistration;
      let registration;
      
      // Priority 1: Check if loading a specific registration (for resubmission)
      if (registrationIdFromState) {
        // Use getRegistrationDetails - it fetches ALL fields including nested data
        registration = await registrationService.getRegistrationDetails(registrationIdFromState);
        
        // Check if it's a rejected registration
        if (registration?.status === 'rejected') {
          setIsResubmission(true);
          setRejectionReason(registration.rejection_reason);
          setRegistrationStatus('rejected');
          // Keep status as 'rejected' but allow editing
          // Status will be reset to 'draft' only when user actually resubmits
        }
      } else if (isNewRegistration) {
        // Priority 2: If this is explicitly a new registration, create a new draft immediately
        const pocName = localStorage.getItem('userPocName') || 'Coach';
        registration = await registrationService.createMinimalDraft(mobileNumber, pocName);
        setRegistrationId(registration?.id);
        setLastSaved(new Date());
        
        // Reset form to empty state
        setFormData({
          ageCategory: '',
          schoolName: '',
          schoolId: null,
          schoolEmail: '',
          city: '',
          primaryCoachName: '',
          primaryCoachMobile: mobileNumber,
          primaryCoachEmail: '',
          primaryCoachTshirtSize: '',
          hasSecondCoach: false,
          secondCoachName: '',
          secondCoachMobile: '',
          secondCoachEmail: '',
          secondCoachTshirtSize: '',
          players: [],
          captainId: ''
        });
        setCurrentStep(1);
        return; // Return early, form starts fresh
      } else {
        // Priority 3: Try to get draft by mobile (for continuing existing draft)
        registration = await registrationService.getDraftByMobile(mobileNumber);
        
        // If no draft exists, create a minimal one immediately
        if (!registration) {
          const pocName = localStorage.getItem('userPocName') || 'Coach';
          registration = await registrationService.createMinimalDraft(mobileNumber, pocName);
          setRegistrationId(registration?.id);
          setLastSaved(new Date());
          return; // Return early, form starts fresh
        }
        
        // Load full details for draft too (to get nested data)
        registration = await registrationService.getRegistrationDetails(registration.id);
      }

      // Now use 'registration' (from getRegistrationDetails) which has ALL data from Supabase
      setRegistrationId(registration?.id);
      setRegistrationStatus(registration?.status);
      
      // Load players separately (fresh from DB)
      const players = await registrationService.getPlayersByRegistration(registration.id);
      
      // Load coaches separately (fresh from DB)
      const coaches = await registrationService.getCoachesByRegistration(registration.id);
      
      // Find primary and secondary coaches
      const primaryCoach = coaches?.find(c => c.is_primary);
      const secondaryCoach = coaches?.find(c => !c.is_primary);
      
      // Map ALL fields from Supabase to form structure
      setFormData({
        ageCategory: registration?.age_category || '',
        schoolName: registration?.schools?.name || '', // From nested schools
        schoolId: registration?.school_id,
        schoolEmail: registration?.school_email || '',
        city: registration?.city || '',
        primaryCoachName: primaryCoach?.name || '',
        primaryCoachMobile: primaryCoach?.phone || registration?.mobile_number || mobileNumber,
        primaryCoachEmail: primaryCoach?.email || registration?.poc_email || '',
        primaryCoachTshirtSize: primaryCoach?.tshirt_size || '',
        hasSecondCoach: !!secondaryCoach,
        secondCoachName: secondaryCoach?.name || '',
        secondCoachMobile: secondaryCoach?.phone || '',
        secondCoachEmail: secondaryCoach?.email || '',
        secondCoachTshirtSize: secondaryCoach?.tshirt_size || '',
        players: players?.map(p => ({
          id: p?.id,
          dbId: p?.id, // Store database ID
          name: `${p?.first_name} ${p?.middle_name || ''} ${p?.last_name}`.trim(),
          firstName: p?.first_name,
          middleName: p?.middle_name,
          lastName: p?.last_name,
          dob: p?.date_of_birth,
          mobile: p?.mobile_number,
          email: p?.email,
          mcaId: p?.mca_id,
          role: p?.role,
          tshirtSize: p?.tshirt_size,
          pantSize: p?.pant_size,
          isCaptain: p?.is_captain
        })) || [],
        captainId: registration?.captain_player_id || ''
      });
      
      // Determine current step based on completion
      if (players?.length >= 11) {
        setCurrentStep(4); // Captain selection
      } else if (players?.length > 0) {
        setCurrentStep(3); // Player registration
      } else if (coaches?.length > 0) {
        setCurrentStep(2); // Coach information
      } else if (registration?.school_id) {
        setCurrentStep(2); // School details complete
      } else {
        setCurrentStep(1); // Start from beginning
      }
      
      setLastSaved(new Date(registration.updated_at));
    } catch (error) {
      console.error('Error loading registration:', error);
      setError('Failed to load registration. Please try again.');
    }
  };

  const saveDraft = async () => {
    try {
      setIsSaving(true);
      setError(null);

      if (!registrationId) {
        // This should rarely happen now since we create minimal draft on mount
        const newRegistration = await registrationService?.createDraft(
          mobileNumber,
          formData?.primaryCoachName || 'Coach',
          formData?.primaryCoachEmail || null,
          formData?.ageCategory || 'boys_u14',
          formData?.schoolId || null,
          formData?.schoolEmail || 'placeholder@draft.com',
          formData?.city || 'mumbai'
        );
        setRegistrationId(newRegistration?.id);
        setLastSaved(new Date());
      } else {
        // Check if this is a resubmission and if declaration needs to be cleared
        const needsDeclarationClear = isResubmission && registrationStatus === 'rejected';
        
        // Update existing registration with current form data
        const updates = {
          poc_name: formData?.primaryCoachName || 'Coach',
          poc_email: formData?.primaryCoachEmail || null
        };

        // Only update non-placeholder values
        if (formData?.ageCategory) updates.age_category = formData?.ageCategory;
        if (formData?.schoolId) updates.school_id = formData?.schoolId;
        if (formData?.schoolEmail && formData?.schoolEmail !== 'placeholder@draft.com') {
          updates.school_email = formData?.schoolEmail;
        }
        if (formData?.city) updates.city = formData?.city;

        await registrationService?.updateRegistration(registrationId, updates);
        
        // Save captain if selected
        if (formData?.captainId && registrationId) {
          try {
            await registrationService.setCaptain(registrationId, formData.captainId);
          } catch (error) {
            console.error('Error saving captain in draft:', error);
            // Don't throw - captain save failure shouldn't block draft save
          }
        }
        
        // Clear signed declaration if this is a resubmission (data has changed)
        if (needsDeclarationClear) {
          try {
            await registrationService.removeSignedDeclaration(registrationId);
            // Declaration cleared - user will need to re-download and re-upload
          } catch (error) {
            console.error('Error clearing declaration:', error);
            // Don't block save if clearing fails
          }
        }
        
        setLastSaved(new Date());
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving draft:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Error saving draft';
      if (error?.message) {
        if (error.message.includes('Cannot coerce the result to a single JSON object')) {
          errorMessage = 'Unable to save registration. Please refresh the page and try again.';
        } else if (error.message.includes('not found') || error.message.includes('cannot be updated')) {
          errorMessage = 'Registration not found. Please refresh the page to reload your draft.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      setIsSaving(false);
    }
  };

  const handleChange = async (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-save captain when selected
    if (field === 'captainId' && value && registrationId) {
      try {
        await registrationService.setCaptain(registrationId, value);
        // Captain saved successfully
      } catch (error) {
        console.error('Error auto-saving captain:', error);
        // Keep the value in formData even if save fails
        // User can retry by clicking Save Draft or Complete Registration
      }
    }
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.ageCategory) newErrors.ageCategory = 'Please select an age category';
        if (!formData?.schoolId) newErrors.schoolName = 'Please select your school';
        if (!formData?.schoolEmail) newErrors.schoolEmail = 'School email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.schoolEmail)) {
          newErrors.schoolEmail = 'Please enter a valid email address';
        }
        if (!formData?.city) newErrors.city = 'Please select city';
        break;

      case 2:
        if (!formData?.primaryCoachName) newErrors.primaryCoachName = 'Coach name is required';
        if (!formData?.primaryCoachMobile) newErrors.primaryCoachMobile = 'Mobile number is required';
        else if (!/^\d{10}$/?.test(formData?.primaryCoachMobile)) {
          newErrors.primaryCoachMobile = 'Please enter a valid 10-digit mobile number';
        }
        if (!formData?.primaryCoachTshirtSize) newErrors.primaryCoachTshirtSize = 'T-shirt size is required';

        if (formData?.hasSecondCoach) {
          if (formData?.secondCoachMobile && !/^\d{10}$/?.test(formData?.secondCoachMobile)) {
            newErrors.secondCoachMobile = 'Please enter a valid 10-digit mobile number';
          }
        }
        break;

      case 3:
        if (formData?.players?.length < 11) {
          newErrors.players = { general: 'Minimum 11 players required' };
        } else {
          const playerErrors = {};
          const mobileNumbers = new Set();
          const emails = new Set();

          formData?.players?.forEach((player, index) => {
            const playerError = {};

            if (!player?.firstName) playerError.firstName = 'First name is required';
            if (!player?.lastName) playerError.lastName = 'Last name is required';
            if (!player?.dob) playerError.dob = 'Date of birth is required';
            if (!player?.mobile) {
              playerError.mobile = 'Mobile number is required';
            } else if (!/^\d{10}$/?.test(player?.mobile)) {
              playerError.mobile = 'Invalid mobile number';
            } else if (mobileNumbers?.has(player?.mobile)) {
              playerError.mobile = 'Mobile number already used';
            } else {
              mobileNumbers?.add(player?.mobile);
            }

            if (!player?.email) {
              playerError.email = 'Email is required';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(player?.email)) {
              playerError.email = 'Invalid email address';
            } else if (emails?.has(player?.email)) {
              playerError.email = 'Email already used';
            } else {
              emails?.add(player?.email);
            }
            if (!player?.role) playerError.role = 'Player role is required';
            if (!player?.tshirtSize) playerError.tshirtSize = 'T-shirt size is required';
            if (!player?.pantSize) playerError.pantSize = 'Pant size is required';

            if (Object.keys(playerError)?.length > 0) {
              playerErrors[player.id] = playerError;
            }
          });

          if (Object.keys(playerErrors)?.length > 0) {
            newErrors.players = playerErrors;
          }
        }
        break;

      case 4:
        if (!formData?.captainId) newErrors.captainId = 'Please select a team captain';
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const saveCoaches = async () => {
    if (!registrationId) return;

    // Set mobile session at the start to ensure RLS policies work correctly
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (mobileNumber) {
      await setMobileSession(mobileNumber);
    }

    try {
      // Save primary coach
      if (formData?.primaryCoachName && formData?.primaryCoachTshirtSize) {
        const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
        const primaryCoach = existingCoaches?.find(c => c?.is_primary);

        const primaryCoachData = {
          name: formData?.primaryCoachName,
          tshirtSize: formData?.primaryCoachTshirtSize,
          isPrimary: true,
          email: formData?.primaryCoachEmail || null,
          phone: formData?.primaryCoachMobile || null
        };

        if (primaryCoach) {
          await registrationService?.updateCoach(primaryCoach?.id, primaryCoachData);
        } else {
          await registrationService?.addCoach(registrationId, primaryCoachData);
        }
      }

      // Save second coach if exists
      if (formData?.hasSecondCoach && formData?.secondCoachName && formData?.secondCoachTshirtSize) {
        const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
        const secondCoach = existingCoaches?.find(c => !c?.is_primary);

        const secondCoachData = {
          name: formData?.secondCoachName,
          tshirtSize: formData?.secondCoachTshirtSize,
          isPrimary: false,
          email: formData?.secondCoachEmail || null,
          phone: formData?.secondCoachMobile || null
        };

        if (secondCoach) {
          await registrationService?.updateCoach(secondCoach?.id, secondCoachData);
        } else {
          await registrationService?.addCoach(registrationId, secondCoachData);
        }
      } else if (!formData?.hasSecondCoach) {
        // Remove second coach if it was removed
        const existingCoaches = await registrationService?.getCoachesByRegistration(registrationId);
        const secondCoach = existingCoaches?.find(c => !c?.is_primary);
        if (secondCoach) {
          await registrationService?.deleteCoach(secondCoach?.id);
        }
      }
    } catch (error) {
      console.error('Error saving coaches:', error);
      throw error;
    }
  };

  const handleNext = async () => {
    const isValid = validateStep(currentStep);
    
    if (!isValid) {
      // Log validation errors for debugging
      console.log('Validation failed for step', currentStep, 'Errors:', errors);
      return;
    }
    
    // Save draft before proceeding
    await saveDraft();
    
    // Save coaches if on step 2 (Coach Information)
    if (currentStep === 2) {
      await saveCoaches();
    }
    
    // Reload players from database when moving to step 4 (Captain Selection)
    if (currentStep === 3 && registrationId) {
      try {
        const players = await registrationService?.getPlayersByRegistration(registrationId);
        const playersWithName = players?.map(p => ({
          id: p?.id,
          dbId: p?.id,
          name: `${p?.first_name} ${p?.middle_name || ''} ${p?.last_name}`.trim(),
          firstName: p?.first_name,
          middleName: p?.middle_name,
          lastName: p?.last_name,
          dob: p?.date_of_birth,
          mobile: p?.mobile_number,
          email: p?.email,
          mcaId: p?.mca_id,
          role: p?.role,
          tshirtSize: p?.tshirt_size,
          pantSize: p?.pant_size,
          isCaptain: p?.is_captain
        })) || [];
        
        setFormData(prev => ({
          ...prev,
          players: playersWithName
        }));
      } catch (error) {
        console.error('Error reloading players:', error);
      }
    }
    
    if (currentStep === 4) {
      // Set captain before navigating
      if (formData?.captainId && registrationId) {
        try {
          await registrationService?.setCaptain(registrationId, formData?.captainId);
        } catch (error) {
          console.error('Error setting captain:', error);
          setError('Failed to save captain selection. Please try again.');
          return; // Don't proceed if captain save fails
        }
      }
      
      // If this is a resubmission, reset the status to 'draft' before proceeding
      if (isResubmission && registrationStatus === 'rejected' && registrationId) {
        try {
          await registrationService.resetRegistrationForResubmission(registrationId);
          setIsResubmission(false);
          setRejectionReason(null);
          setRegistrationStatus('draft');
        } catch (error) {
          console.error('Error resetting registration for resubmission:', error);
          setError('Failed to reset registration status. Please try again.');
          return;
        }
      }
      
      navigate('/document-upload-center', { state: { registrationId } });
    } else {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <SchoolDetailsStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 2:
        return (
          <CoachInformationStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
            registrationId={registrationId}
            onSave={saveDraft}
          />
        );
      case 3:
        return (
          <PlayerRegistrationStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
            registrationId={registrationId}
            onPlayerAdded={() => setLastSaved(new Date())}
            onPlayerRemoved={() => setLastSaved(new Date())}
          />
        );
      case 4:
        return (
          <CaptainSelectionStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground py-6 lg:py-8 shadow-md">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/registration-dashboard')}
                className="p-2 hover:bg-primary-foreground/10 rounded-lg transition-colors"
                aria-label="Go back to dashboard"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold">Team Registration</h1>
                <p className="text-sm lg:text-base opacity-90 mt-1">
                  MI Junior Cricket Tournament 2026
                </p>
              </div>
            </div>
            {lastSaved && (
              <div className="hidden lg:flex items-center gap-2 text-sm opacity-80">
                <Icon name="Check" size={16} />
                <span>Last saved: {lastSaved?.toLocaleTimeString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-4xl mx-auto px-4 lg:px-6 mt-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <div>
                <p className="text-sm font-medium text-destructive-foreground">
                  Error saving draft
                </p>
                <p className="text-xs text-muted-foreground mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isResubmission && rejectionReason && (
        <div className="max-w-4xl mx-auto px-4 lg:px-6 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Resubmitting Rejected Registration
                </h3>
                <p className="text-xs text-amber-800 mb-2">
                  <span className="font-medium">Previous rejection reason:</span> {rejectionReason}
                </p>
                <p className="text-xs text-amber-800 mb-2">
                  <span className="font-medium">Important:</span> After making changes, you must re-download the declaration PDF and re-upload a new signed declaration with the updated information. The previous signed declaration has been cleared.
                </p>
                <p className="text-xs text-amber-800">
                  The registration status will be reset to draft when you proceed to document upload.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <RegistrationProgressIndicator currentStep={currentStep} />

        <div className="mt-6 lg:mt-8">
          {renderStepContent()}
        </div>
      </div>

      <FormNavigationControls
        currentStep={currentStep}
        totalSteps={4}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSaveDraft={saveDraft}
        isNextDisabled={false}
        isSaving={isSaving}
      />
    </div>
  );
};

export default TeamRegistrationForm;